import graph from '@/lib/relation-graph.json'
import { withStage } from '@/lib/layered'
import type { DB } from '@/lib/kysely-d'
import type { Kysely, ReferenceExpression } from 'kysely'
import { computeDepths } from '@/lib/utils/relation-graph-utils'
import type { Edge } from '@/lib/utils/relation-graph-utils'

type TableName = keyof DB
type ColumnOf<T extends TableName> = keyof DB[T]
type Relation<F extends TableName, T extends TableName> = {
  from_table: F
  from: ColumnOf<F>
  to_table: T
  to: ColumnOf<T>
}

/**
 * Retrieves all foreign key relationships for a given table.
 *
 * @template FT - The type representing the table name.
 * @param {FT} table - The name of the table for which to retrieve foreign key relationships.
 * @returns {Array<Relation<FT, TableName>>} - An array of relations representing the foreign key relationships from the specified table to other tables.
 */
export const foreignKeyOf = <FT extends TableName>(table: FT): Array<Relation<FT, TableName>> => {
  return graph.filter((x) => x.from_table === table) as unknown as Array<Relation<FT, TableName>>
}

/**
 * Retrieves all relationships where the specified table is referenced by other tables.
 *
 * @template TT - The name of the table being checked for references (extends TableName).
 * @param table - The name of the table for which references are to be retrieved.
 * @returns An array of `Relation` objects, representing all relationships where the specified
 *          table is the target (`to_table`) of a foreign key.
 */
export const reverseForeignKeyOf = <TT extends TableName>(
  table: TT
): Array<Relation<TableName, TT>> => {
  return graph.filter((x) => x.to_table === table) as unknown as Array<Relation<TableName, TT>>
}

/**
 * Recursively calculates the depth of a table in a foreign key hierarchy.
 *
 * @template FT - The type representing the table name.
 * @param {FT} table - The name of the table for which to calculate depth.
 * @returns {number} - The depth of the table, where depth is the maximum number of foreign key relationships between the table and its dependent tables.
 *                    A table with no foreign key relations will have a depth of 0.
 */
const { depthOf: depthOfInternal } = computeDepths(graph as unknown as Edge[])
export const depthOf = <FT extends TableName>(table: FT): number => {
  return depthOfInternal(table as string)
}

/**
 * Creates a function to check whether all provided IDs exist in a specified database table.
 *
 * @example
 * const checkAllExist = isExistAll('users');
 * const result = await checkAllExist(['id1', 'id2', 'id3']);
 * console.log(result); // true if all IDs exist, false otherwise
 *
 * @param table - The name of the database table to query.
 * @param key
 * @returns A function that accepts an array of string IDs and returns a Promise resolving to a boolean.
 */
export const isExistAll =
  (table: keyof DB, key: ReferenceExpression<DB, keyof DB> = 'id') =>
  async (ids: string[]) => {
    if (ids.length === 0) return true
    const xs = await withStage(async (db: Kysely<DB>) => {
      return await db.selectFrom(table).where(key, 'in', ids).selectAll().execute()
    })
    return xs.length === ids.length
  }

/**
 * Creates a function to check if a specific ID exists in a specified database table.
 *
 * @example
 * const checkExist = isExist('users');
 * const result = await checkExist('id1');
 * console.log(result); // true if the ID exists, false otherwise
 *
 * @param table - The name of the database table to query.
 * @param key
 * @returns A function that accepts a string ID and returns a Promise resolving to a boolean.
 */
export const isExist =
  (table: keyof DB, key: ReferenceExpression<DB, keyof DB> = 'id') =>
  async (id: string) => {
    const xs = await withStage(async (db: Kysely<DB>) => {
      return await db.selectFrom(table).where(key, '=', id).selectAll().execute()
    })
    return xs.length == 1
  }
